import { useEffect, useRef, useState } from "react";
import type { CardColor } from "@shared/cards";
import type { LobbySeat } from "@shared/events";
import { Card } from "../components/Card";
import { paint } from "../components/SeatRing";
import { useWakeLock } from "../wakeLock";
import { canPlayCard } from "../canPlay";
import { getMuted, setMuted } from "../audio";
import { clearToken, clearLastCode } from "../storage";
import type { GameStore } from "../gameStore";

export function PhcView({ store }: { store: GameStore }): JSX.Element {
  const [muted, setMutedState] = useState(getMuted());
  const [hideHand, setHideHand] = useState(false);
  const [pendingWildId, setPendingWildId] = useState<string | null>(null);

  const lobby = store.lobby;
  const view = store.tdcView;
  const phase = view?.phase ?? lobby?.phase ?? "LOBBY";
  // Stay awake for the whole game session — only release once the game has ended.
  useWakeLock(phase !== "ENDED");
  const me = lobby?.seats.find((s) => s.participantId === store.welcome?.myParticipantId);
  const mySeatIndex = me?.seatIndex ?? null;
  const isMyTurn = view ? view.currentSeatIndex === mySeatIndex : false;

  useEffect(() => {
    if (isMyTurn) {
      // turn-change cue (audio handled in store on first_player_selected; reuse here for fairness)
    }
  }, [isMyTurn]);

  const showRedJoin = !lobby;
  const phcLobbyMode = phase === "LOBBY" || phase === "COUNTDOWN";

  const handleLeave = (): void => {
    if (!window.confirm("Leave the game?")) return;
    const code = store.welcome?.gameCode;
    if (code) clearToken(code);
    clearLastCode();
    window.location.href = "/";
  };

  return (
    <div className="phc-root">
      <div className="phc-header">
        <span style={{ fontWeight: 700 }}>{me?.name ?? "Player"}</span>
        {me?.color && <span className="badge" style={{ background: paint(me.color) }} aria-hidden />}
        <span className="muted">· code {store.welcome?.gameCode}</span>
        <span
          className={`right status-dot status-${store.socketStatus}`}
          title={store.socketStatus}
          aria-label={`connection: ${store.socketStatus}`}
        />
        <KebabMenu
          muted={muted}
          onToggleMute={() => {
            const next = !muted;
            setMutedState(next);
            setMuted(next);
          }}
          onLeave={handleLeave}
        />
      </div>

      <div className="phc-main">
        {showRedJoin && <div className="muted">connecting…</div>}

        {phcLobbyMode && lobby && (
          <PhcLobby store={store} mySeatIndex={mySeatIndex} />
        )}

        {!phcLobbyMode && view && (
          <PhcGame
            store={store}
            mySeatIndex={mySeatIndex}
            isMyTurn={isMyTurn}
            hideHand={hideHand}
            setHideHand={setHideHand}
            setPendingWildId={setPendingWildId}
          />
        )}

        {store.welcome?.isWaiting && phase !== "LOBBY" && phase !== "COUNTDOWN" && (
          <div className="card">
            <div className="muted">Waiting for the next round…</div>
          </div>
        )}
      </div>

      {pendingWildId && (
        <ColorPicker
          onPick={(color) => {
            store.emit("play_card", { cardId: pendingWildId, colorChoice: color });
            setPendingWildId(null);
          }}
        />
      )}

      {mySeatIndex !== null &&
        store.tdcView?.awaitingColorBy === mySeatIndex &&
        !pendingWildId && (
          <ColorPicker onPick={(color) => store.emit("wild_color_choose", { color })} />
        )}

      {store.unoChallenge &&
        store.unoChallenge.challengedSeatIndex !== mySeatIndex && (
          <UnoChallengeButton
            onCallOut={() =>
              store.emit("challenge_uno", {
                targetSeatIndex: store.unoChallenge!.challengedSeatIndex,
              })
            }
            deadlineMs={store.unoChallenge.deadlineMs}
          />
        )}

      {store.rejectMessage && <div className="toast">{store.rejectMessage}</div>}

      {store.ended && (
        <div className="modal-backdrop">
          <div className="modal col">
            <div className="brand">Game over</div>
            <div className="muted">{store.ended.reason.replace(/_/g, " ")}</div>
            <button onClick={() => window.location.reload()}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

function PhcLobby({
  store,
  mySeatIndex,
}: {
  store: GameStore;
  mySeatIndex: number | null;
}): JSX.Element {
  const lobby = store.lobby!;
  const SEATS_VISIBLE = 8;
  const seats = lobby.seats.slice(0, SEATS_VISIBLE);
  const me = lobby.seats.find((s) => s.participantId === store.welcome?.myParticipantId);

  return (
    <>
      {lobby.phase === "COUNTDOWN" ? (
        <div style={{ fontSize: "6rem", fontWeight: 800, color: "var(--accent)" }}>
          {store.countdownSeconds ?? 3}
        </div>
      ) : (
        <>
          <div className="phc-seat-prompt">
            {mySeatIndex === null
              ? "Look at the table — tap the number of the seat you're sitting in."
              : `You're in seat ${mySeatIndex + 1}. Hit Ready when you're set.`}
          </div>
          <div className="seat-grid" role="list">
            {seats.map((s: LobbySeat) => {
              const isMine = s.participantId && s.participantId === store.welcome?.myParticipantId;
              const taken = !!s.participantId;
              const tappable = !taken && mySeatIndex === null;
              const cls = [
                "seat-grid-item",
                taken ? "taken" : "empty",
                isMine ? "mine" : "",
                s.isReady ? "ready" : "",
                tappable ? "tappable" : "",
              ]
                .filter(Boolean)
                .join(" ");
              return (
                <button
                  type="button"
                  role="listitem"
                  key={s.seatIndex}
                  className={cls}
                  disabled={!tappable}
                  onClick={
                    tappable
                      ? () => store.emit("claim_seat", { seatIndex: s.seatIndex })
                      : undefined
                  }
                >
                  <span className="seat-grid-number">{s.seatIndex + 1}</span>
                  <span className="seat-grid-name">
                    {isMine ? "You" : s.name ?? (taken ? "Player" : "empty")}
                  </span>
                </button>
              );
            })}
          </div>
          {mySeatIndex !== null && (
            <div className="action-bar">
              <button
                className={`primary-cta ${me?.isReady ? "danger" : "success"}`}
                onClick={() => store.emit("set_ready", { ready: !me?.isReady })}
              >
                {me?.isReady ? "Not ready" : "Ready"}
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
}

function KebabMenu({
  muted,
  onToggleMute,
  onLeave,
}: {
  muted: boolean;
  onToggleMute: () => void;
  onLeave: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="kebab" ref={ref}>
      <button
        className="ghost kebab-trigger"
        aria-label="Menu"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        ⋮
      </button>
      {open && (
        <div className="kebab-menu" role="menu">
          <button
            role="menuitem"
            className="kebab-item"
            onClick={() => {
              onToggleMute();
              setOpen(false);
            }}
          >
            <span aria-hidden>{muted ? "🔊" : "🔇"}</span>
            <span>{muted ? "Unmute" : "Mute"}</span>
          </button>
          <button
            role="menuitem"
            className="kebab-item kebab-item-danger"
            onClick={() => {
              setOpen(false);
              onLeave();
            }}
          >
            <span aria-hidden>↩</span>
            <span>Leave game</span>
          </button>
        </div>
      )}
    </div>
  );
}

function PhcGame({
  store,
  mySeatIndex,
  isMyTurn,
  hideHand,
  setHideHand,
  setPendingWildId,
}: {
  store: GameStore;
  mySeatIndex: number | null;
  isMyTurn: boolean;
  hideHand: boolean;
  setHideHand: (v: boolean) => void;
  setPendingWildId: (id: string | null) => void;
}): JSX.Element {
  const view = store.tdcView!;
  const top = view.topOfDiscard;
  const myDeclaredUno = view.seats.find((s) => s.seatIndex === mySeatIndex)?.declaredUno;
  const oneCardLeft = store.hand.length === 1;
  const currentSeat = view.seats.find((s) => s.seatIndex === view.currentSeatIndex);

  const onCardTap = (cardId: string, isWild: boolean): void => {
    if (!isMyTurn) return;
    if (view.awaitingColorBy !== null) return;
    if (isWild) {
      setPendingWildId(cardId);
      return;
    }
    store.emit("play_card", { cardId });
  };

  return (
    <div className="col" style={{ width: "100%", alignItems: "center" }}>
      <div className={`turn-banner ${isMyTurn ? "my-turn" : "not-my-turn"}`}>
        {isMyTurn ? (
          <span>Your turn</span>
        ) : (
          <>
            <span
              className="turn-badge"
              style={{ background: paint(currentSeat?.color) }}
              aria-hidden
            />
            <span>{currentSeat?.name ?? `Seat ${view.currentSeatIndex + 1}`}'s turn</span>
          </>
        )}
        <span className="turn-meta">round {view.roundNumber} · {view.direction === 1 ? "↻" : "↺"}</span>
      </div>

      <div className="row" style={{ justifyContent: "center", gap: "1rem" }}>
        <div className="pile">
          <div className="pile-label">Top</div>
          {top ? <Card card={top} size="small" /> : <div className="card-back" />}
          {view.currentColor && (
            <div className="muted" style={{ textTransform: "capitalize" }}>
              {view.currentColor}
            </div>
          )}
        </div>
      </div>

      <div className={`phc-hand ${isMyTurn ? "" : "waiting"}`}>
        {store.hand.map((card) => {
          const playable = isMyTurn && canPlayCard(card, top, view.currentColor);
          if (hideHand) {
            return (
              <div className="card-face" key={card.id} style={{ background: "#475569" }}>
                <div className="symbol">?</div>
              </div>
            );
          }
          return (
            <Card
              key={card.id}
              card={card}
              playable={playable}
              unplayable={isMyTurn && !playable}
              onClick={() => onCardTap(card.id, card.color === "wild")}
            />
          );
        })}
      </div>

      <div className="phc-controls">
        {view.phase === "REPLAY_PROMPT" && (() => {
          const myVote = view.replayVotes.find((v) => v.seatIndex === mySeatIndex)?.vote ?? null;
          const totalSeats = view.replayVotes.length;
          const cast = view.replayVotes.filter((v) => v.vote !== null).length;
          if (myVote === null) {
            return (
              <div className="phc-controls-row">
                <button className="success" onClick={() => store.emit("vote_replay", { vote: true })}>
                  Play again
                </button>
                <button className="danger" onClick={() => store.emit("vote_replay", { vote: false })}>
                  Stop
                </button>
              </div>
            );
          }
          return (
            <div className="phc-controls-row replay-waiting">
              <span className="muted">
                You voted <strong>{myVote ? "Play again" : "Stop"}</strong> · {cast}/{totalSeats} voted ·
                {" "}waiting for everyone…
              </span>
            </div>
          );
        })()}
        <div className="phc-controls-row">
          <button className="ghost" onClick={() => setHideHand(!hideHand)}>
            {hideHand ? "Show hand" : "Hide hand"}
          </button>
          <button
            onClick={() => store.emit("draw_card")}
            disabled={!isMyTurn || view.phase !== "PLAYING"}
          >
            Draw
          </button>
          <button
            onClick={() => store.emit("declare_uno")}
            disabled={myDeclaredUno || !oneCardLeft}
            className={oneCardLeft && !myDeclaredUno ? "success" : ""}
          >
            UNO!
          </button>
        </div>
      </div>
    </div>
  );
}

function ColorPicker({
  onPick,
}: {
  onPick: (color: Exclude<CardColor, "wild">) => void;
}): JSX.Element {
  return (
    <div className="modal-backdrop">
      <div className="modal col">
        <div className="brand">Pick a color</div>
        <div className="color-grid">
          <button onClick={() => onPick("red")} style={{ background: "#ef4444" }}>Red</button>
          <button onClick={() => onPick("yellow")} style={{ background: "#f59e0b", color: "#1a1100" }}>Yellow</button>
          <button onClick={() => onPick("green")} style={{ background: "#22c55e" }}>Green</button>
          <button onClick={() => onPick("blue")} style={{ background: "#3b82f6" }}>Blue</button>
        </div>
      </div>
    </div>
  );
}

function UnoChallengeButton({
  onCallOut,
  deadlineMs,
}: {
  onCallOut: () => void;
  deadlineMs: number;
}): JSX.Element {
  const [ms, setMs] = useState(Math.max(0, deadlineMs - Date.now()));
  useEffect(() => {
    const t = setInterval(() => setMs(Math.max(0, deadlineMs - Date.now())), 100);
    return () => clearInterval(t);
  }, [deadlineMs]);
  if (ms <= 0) return <></>;
  return (
    <div style={{ position: "fixed", bottom: 100, left: 0, right: 0, display: "flex", justifyContent: "center" }}>
      <button onClick={onCallOut} className="danger" style={{ padding: "1rem 2rem" }}>
        Call out! ({Math.ceil(ms / 1000)}s)
      </button>
    </div>
  );
}
